import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocPortal, MapLocKeys } from '@cm/core/localization';
import { achievementsDPRoot } from '../achievements-meta';

const dpRoot = `${achievementsDPRoot}|Status`;

const sharedProps: Pick<LocDef, 'htmlAllowed' | 'shared' | 'portal' | 'feature'> = {
	feature: HubFeature.CredentialAchievements,
	portal: 'CP' as LocPortal,
	shared: false,
	htmlAllowed: false,
};

export type AchievementEcertLoc =
	| 'CPAchievementEcertExpired'
	| 'CPAchievementEcertActive'
	| 'CPAchievementEcertNever'
	| 'CPAchievementEcertName'
	| 'CPAchievementEcertGroup'
	| 'CPAchievementEcertStatus'
	| 'CPAchievementEcertActiveDate'
	| 'CPAchievementEcertExpireDate'
	| 'CPAchievementEcertActions'
	| 'CPAchievementEcertDownloadButton'
	| 'CPAchievementEcertDownloadTooltip'
	| 'CPAchievementEcertPageTitle'
	| 'CPAchievementEcertPageDescription';

export const achievementEcertLocs: LocDefs<AchievementEcertLoc> = {
	CPAchievementEcertExpired: {
		defaultValue: 'Expired',
		description: 'Content shown for the status of an achievement when it is already expired',
		displayPath: `${dpRoot}|Expired`,
		displayType: 'Content',
		legacyName: 'Credential.Achievements/Credentials.Achievements.Status.Content.Expired',
		...sharedProps,
	},
	CPAchievementEcertActive: {
		defaultValue: 'Active',
		description: 'Content shown for the status of an achievement when it is active',
		displayPath: `${dpRoot}|Active`,
		displayType: 'Content',
		legacyName: 'Credential.Achievements/Credentials.Achievements.Status.Content.Active',
		...sharedProps,
	},
	CPAchievementEcertNever: {
		defaultValue: 'Never',
		description: 'Content shown when there is no expire date for the achievement',
		displayPath: `${dpRoot}|Never`,
		displayType: 'Content',
		legacyName: 'Credential.Achievements/Credentials.Achievements.Status.Content.Never',
		...sharedProps,
	},
	CPAchievementEcertName: {
		defaultValue: 'Name',
		description: 'Label for the achievement name',
		displayPath: `${dpRoot}|Name`,
		displayType: 'Label',
		legacyName: 'Credential.Achievements/Credentials.Achievements.Status.FieldLabel.Name',
		...sharedProps,
	},
	CPAchievementEcertGroup: {
		defaultValue: 'Group',
		description: 'Label for the achievement group',
		displayPath: `${dpRoot}|Group`,
		displayType: 'Label',
		legacyName: 'Credential.Achievements/Credentials.Achievements.Status.FieldLabel.Group',
		...sharedProps,
	},
	CPAchievementEcertStatus: {
		defaultValue: 'Status',
		description: 'Field label for status',
		displayPath: `${dpRoot}|Status`,
		displayType: 'Label',
		legacyName: 'Credential.Achievements/Credentials.Achievements.Status.FieldLabel.Status',
		...sharedProps,
	},
	CPAchievementEcertActiveDate: {
		defaultValue: 'Active Date',
		description: 'Field label for active date',
		displayPath: `${dpRoot}|Active Date`,
		displayType: 'Label',
		legacyName: 'Credential.Achievements/Credentials.Achievements.Status.FieldLabel.ActiveDate',
		...sharedProps,
	},
	CPAchievementEcertExpireDate: {
		defaultValue: 'Expire Date',
		description: 'Field label for expire date',
		displayPath: `${dpRoot}|Expire Date`,
		displayType: 'Label',
		legacyName: 'Credential.Achievements/Credentials.Achievements.Status.FieldLabel.ExpireDate',
		...sharedProps,
	},
	CPAchievementEcertActions: {
		defaultValue: '',
		description: 'Field label for actions',
		displayPath: `${dpRoot}|Actions`,
		displayType: 'Label',
		...sharedProps,
	},
	CPAchievementEcertDownloadButton: {
		defaultValue: 'Download',
		description: 'Default text for the download button',
		displayPath: `${dpRoot}|Download Button`,
		displayType: 'Button',
		legacyName:
			'Credential.Achievements.Certificate/Achievements.Ecertificates.Button.Download',
		...sharedProps,
		feature: HubFeature.CredentialAchievementsCertificate,
	},
	CPAchievementEcertDownloadTooltip: {
		defaultValue: 'Download E-Certificate',
		description: 'Content shown in the tooltip for the download button',
		displayPath: `${dpRoot}|Download Tooltip`,
		displayType: 'Content',
		legacyName:
			'Credential.Achievements.Certificate/Credentials.Achievements.Status.Content.DownloadEcert',
		...sharedProps,
		feature: HubFeature.CredentialAchievementsCertificate,
	},
	CPAchievementEcertPageTitle: {
		defaultValue: 'Achievements',
		description: 'Page title for achievements status page. This is shared with the tab title.',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		legacyName: 'Credential.Achievements/Credentials.Achievements.Status.Title.Pagetitle',
		...sharedProps,
	},
	CPAchievementEcertPageDescription: {
		defaultValue: '',
		description: 'Page description for achievements status page',
		displayPath: `${dpRoot}|Page Description`,
		displayType: 'Content',
		legacyName:
			'Credential.Achievements/Credentials.Achievements.Status.Content.PageDescription',
		...sharedProps,
	},
};

export const LOC_KEYS = MapLocKeys<AchievementEcertLoc>(achievementEcertLocs);
